import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { getArticleBySlug, articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

const hardcodedImageUrls: Record<string, string> = {
  'nana-lite-launch': 'https://readdy.ai/api/search-image?query=Minimalist%20lightweight%20app%20on%20a%20clean%20white%20desk%20with%20soft%20natural%20lighting%2C%20elegant%20and%20simple%20design%2C%20warm%20cream%20background%2C%20editorial%20product%20photography%20with%20gentle%20shadows&width=1200&height=600&seq=article-detail-launch&orientation=landscape',
  'boost-productivity-with-nana-lite': 'https://readdy.ai/api/search-image?query=Modern%20workspace%20with%20laptop%20showing%20minimalist%20productivity%20app%20interface%2C%20warm%20ambient%20lighting%2C%20clean%20desk%20setup%20with%20notebook%20and%20coffee%2C%20soft%20cream%20and%20wood%20tones&width=1200&height=600&seq=article-detail-productivity&orientation=landscape',
  'lite-vs-full-comparison': 'https://readdy.ai/api/search-image?query=Side%20by%20side%20comparison%20of%20two%20smartphone%20app%20interfaces%2C%20one%20minimalist%20one%20comprehensive%2C%20clean%20white%20background%2C%20soft%20shadow%2C%20modern%20UI%20design%20aesthetic&width=1200&height=600&seq=article-detail-comparison&orientation=landscape',
  'hidden-features': 'https://readdy.ai/api/search-image?query=Smartphone%20showing%20hidden%20gesture%20features%20of%20a%20minimalist%20app%2C%20warm%20cream%20background%2C%20soft%20natural%20light%2C%20clean%20modern%20photography%20style&width=1200&height=600&seq=article-detail-hidden&orientation=landscape',
  'installation-guide': 'https://readdy.ai/api/search-image?query=Laptop%20screen%20showing%20app%20installation%20process%20with%20progress%20bar%2C%20clean%20modern%20desk%20setup%2C%20warm%20ambient%20lighting%2C%20minimalist%20workspace%20aesthetic&width=1200&height=600&seq=article-detail-install&orientation=landscape',
  'minimalist-workflow': 'https://readdy.ai/api/search-image?query=Clean%20minimalist%20desk%20with%20a%20notebook%20and%20a%20phone%20displaying%20productivity%20app%2C%20morning%20sunlight%20through%20window%2C%20warm%20cream%20and%20beige%20tones%2C%20zen%20atmosphere&width=1200&height=600&seq=article-detail-workflow&orientation=landscape',
  'industry-trends-lightweight-apps': 'https://readdy.ai/api/search-image?query=Futuristic%20minimalist%20data%20visualization%20chart%20showing%20upward%20trend%2C%20warm%20cream%20background%20with%20clean%20geometric%20patterns%2C%20modern%20infographic%20style%2C%20soft%20lighting&width=1200&height=600&seq=article-detail-trends&orientation=landscape',
  'nana-lite-mobile-guide': 'https://readdy.ai/api/search-image?query=Smartphone%20in%20hand%20displaying%20minimalist%20app%20interface%20on%20a%20clean%20cafe%20table%2C%20morning%20light%2C%20warm%20cream%20and%20wood%20tones%2C%20lifestyle%20product%20photography&width=1200&height=600&seq=article-detail-mobile&orientation=landscape',
  'efficiency-methods-comparison': 'https://readdy.ai/api/search-image?query=Three%20productivity%20method%20cards%20laid%20out%20on%20a%20clean%20desk%2C%20GTD%20tomato%20timer%20and%20quadrant%20diagram%2C%20warm%20cream%20background%2C%20minimalist%20flat%20lay%20photography&width=1200&height=600&seq=article-detail-methods&orientation=landscape',
  'team-collaboration-lite': 'https://readdy.ai/api/search-image?query=Small%20team%20of%20three%20people%20collaborating%20around%20a%20laptop%20in%20a%20bright%20modern%20office%2C%20warm%20neutral%20tones%2C%20natural%20light%2C%20genuine%20interaction&width=1200&height=600&seq=article-detail-team&orientation=landscape',
  'security-privacy-nana-lite': 'https://readdy.ai/api/search-image?query=Abstract%20digital%20security%20concept%20with%20lock%20icon%20and%20shield%2C%20warm%20cream%20background%20with%20subtle%20geometric%20patterns%2C%20minimalist%20tech%20illustration%20style&width=1200&height=600&seq=article-detail-security&orientation=landscape',
  'user-story-freelancer': 'https://readdy.ai/api/search-image?query=Freelancer%20working%20happily%20in%20a%20cozy%20home%20office%20with%20laptop%20and%20warm%20lighting%2C%20plants%20on%20desk%2C%20relaxed%20authentic%20atmosphere%2C%20cream%20and%20wood%20interior&width=1200&height=600&seq=article-detail-user&orientation=landscape',
};

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');

  if (!article) {
    return (
      <main className="min-h-screen pt-16 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-2xl bg-background-100 text-foreground-400 mb-4">
            <i className="ri-file-unknow-line text-2xl"></i>
          </div>
          <h1 className="text-xl font-semibold text-foreground-950 mb-2">文章未找到</h1>
          <p className="text-sm text-foreground-600 mb-6">该文章可能已被移除或链接有误。</p>
          <Link to="/category/all" className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors">
            返回文章列表
          </Link>
        </div>
      </main>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const categoryName = category ? category.name : article.category;

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '文章分类', href: '/category/all' },
    { label: categoryName, href: `/category/${article.category}` },
    { label: article.title },
  ];

  const articleJsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.excerpt,
      author: {
        '@type': 'Person',
        name: article.author,
      },
      datePublished: article.date,
      publisher: {
        '@type': 'Organization',
        name: '娜娜轻量版',
      },
      url: `https://www.nanalite.com/article/${article.slug}`,
      inLanguage: 'zh-CN',
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbItems.map((item, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        name: item.label,
        item: item.href ? `https://www.nanalite.com${item.href}` : undefined,
      })),
    },
  ];

  return (
    <>
      <SEOHead
        title={`${article.title} — 娜娜轻量版`}
        description={article.excerpt}
        keywords={`娜娜轻量版,${article.tags.join(',')},轻量版应用`}
        canonicalUrl={`https://www.nanalite.com/article/${article.slug}`}
        ogType="article"
        ogTitle={article.title}
        ogDescription={article.excerpt}
        jsonLd={articleJsonLd}
        articleDate={article.date}
        articleAuthor={article.author}
      />
      <main className="min-h-screen pt-16">
        <article className="max-w-4xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          <header className="py-8">
            <div className="flex items-center gap-3 mb-4 text-sm text-foreground-600">
              <span>{article.date}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
              <span>{article.readTime}</span>
              <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
              <Link
                to={`/category/${article.category}`}
                className="text-primary-500 hover:text-primary-600 transition-colors"
              >
                {categoryName}
              </Link>
            </div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold font-heading text-foreground-950 leading-tight mb-4">
              {article.title}
            </h1>
            <p className="flex items-center gap-2 text-sm text-foreground-600">
              <i className="ri-user-line"></i>
              <span>{article.author}</span>
            </p>
          </header>

          <div className="mb-10 rounded-2xl overflow-hidden">
            <img
              src={hardcodedImageUrls[article.slug]}
              alt={article.title}
              title={`${article.title} - 娜娜轻量版`}
              className="w-full h-auto"
            />
          </div>

          <div
            className="prose prose-sm md:prose-base max-w-none pb-10"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          <div className="flex flex-wrap gap-2 pb-12 border-b border-background-200/70 mb-12">
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium"
              >
                #{tag}
              </span>
            ))}
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="bg-background-100 py-14">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-8">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedArticles.map((related) => (
                  <Link
                    key={related.slug}
                    to={`/article/${related.slug}`}
                    className="group block rounded-2xl bg-background-50 overflow-hidden border border-background-200/70 hover:border-primary-200/60 transition-colors duration-200"
                  >
                    <div className="h-40 overflow-hidden">
                      <img
                        src={hardcodedImageUrls[related.slug]}
                        alt={related.title}
                        title={`${related.title} - 娜娜轻量版`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs text-foreground-500">{related.date}</span>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-accent-100 text-accent-700">
                          {related.readTime}
                        </span>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-950 line-clamp-2 group-hover:text-primary-500 transition-colors">
                        {related.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  );
}